import { useState } from "react"
import toast from "react-hot-toast"
import {FileDown} from 'lucide-react'
import type { Equipment } from "../hooks/useEquipments"
import type { EquipmentIssue, EquipmentComment } from "../hooks/useEquipmentIssues"
import { generateEquipmentIssuePdf } from "../lib/equipmentIssuePdf"

interface Props {
  equipment: Equipment
  /** 출력 대상 이슈 1건 */
  issue: EquipmentIssue
  /** 장비 단위로 로드된 전체 코멘트 (해당 이슈 것만 자동 필터링됨) */
  comments: EquipmentComment[]
  unitLabelMap: Map<string, string>
}

/**
 * 이슈 카드 우측 상단의 <출력> 버튼.
 * 표지 없이 해당 이슈 1건만 이슈 발생 / 조치 결과 / 코멘트 구역으로 출력한다.
 */
export function EquipmentIssueSinglePdfButton({
  equipment,
  issue,
  comments,
  unitLabelMap,
}: Props) {
  const [exporting, setExporting] = useState(false)

  const handleExport = async () => {
    if (exporting) return
    setExporting(true)
    const toastId = toast.loading("이슈 PDF를 생성하는 중입니다...")
    try {
      const issueComments = comments.filter((c) => c.issueId === issue._id)
      const title = issue.title?.trim() || "제목없음"
      await generateEquipmentIssuePdf({
        equipment,
        issues: [issue],
        comments: issueComments,
        unitLabelMap,
        includePhotos: true,
        includeComments: true,
        // 개별 출력은 표지/색인표 없이 이슈 본문부터 시작
        withCover: false,
        fileName: `${equipment.productName || "장비"}_${title}`,
      })
      toast.success("이슈 PDF가 저장되었습니다.", { id: toastId })
    } catch (e) {
      console.error("개별 이슈 PDF 생성 실패:", e)
      toast.error("PDF 생성에 실패했습니다. 잠시 후 다시 시도해주세요.", { id: toastId })
    } finally {
      setExporting(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={exporting}
      title="이 이슈만 PDF로 출력"
      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-60 transition-colors"
    >
      {exporting ? (
        <span className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
      ) : (
        <FileDown className="w-3.5 h-3.5" />
      )}
      출력
    </button>
  )
}
